import { computedAsync } from '@vueuse/core'
import { isFunction } from 'lodash'
import { computed, inject, ref, toValue } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import ShortLinkApiHandler from '@/api/short-link'
import { handleError } from '@/utils/error-handling'
import {
  isAzureAppInjectionKey,
  isCrossPlatformAppInjectionKey,
  isJiraAppInjectionKey,
  isWebAppInjectionKey
} from '@/utils/injection-keys'
import { PLUGIN_OPTIONS_KEYS } from '@root/template-options-keys'

export const useCopySharedLinkModal = () => {
  const shareLinkLoading = ref(false)
  const showCopyShareLinkModal = ref(false)
  const shareToken = ref('')
  const route = useRoute()

  const onCopyClick = async () => {
    shareLinkLoading.value = true

    try {
      const api = new ShortLinkApiHandler()
      const data = await api.createShortLink({ link: route.fullPath })
      shareToken.value = data.token
      showCopyShareLinkModal.value = true
    } catch (error) {
      handleError({ error })
    } finally {
      shareLinkLoading.value = false
    }
  }

  return {
    onCopyClick,
    shareToken,
    shareLinkLoading,
    showCopyShareLinkModal
  }
}

export const useLinksList = ({
  jiraLinkGenerator,
  webAppLinkGenerator,
  azureAppLinkGenerator,
  localDevelopmentUrl = ''
} = {}) => {
  if (
    !isFunction(jiraLinkGenerator) ||
    !isFunction(webAppLinkGenerator) ||
    !isFunction(azureAppLinkGenerator)
  ) {
    throw new Error(
      'jiraLinkGenerator, webAppLinkGenerator, azureAppLinkGenerator  must be functions'
    )
  }

  const isJiraApp = inject(isJiraAppInjectionKey)
  const isWebApp = inject(isWebAppInjectionKey)
  const isCrossPlatformApp = inject(isCrossPlatformAppInjectionKey)
  const isAzureApp = inject(isAzureAppInjectionKey)

  const store = useStore()

  const { t } = useI18n()

  const isJiraConnected = computed(() => {
    return store.state.pluginOptions[PLUGIN_OPTIONS_KEYS.JIRA_CONNECTED]
  })

  const isWebAppConnected = computed(() => {
    return store.state.pluginOptions[PLUGIN_OPTIONS_KEYS.WEB_APP_CONNECTED]
  })

  // const hasAccessToJira = computed(() => store.state.system.userData?.hasAccessToJira || false)

  const linksForShare = computedAsync(async () => {
    if (isCrossPlatformApp) {
      return []
    }

    if (isAzureApp) {
      return [
        {
          id: 'azure-app-link',
          label: t('common.direct_link'),
          path: toValue(localDevelopmentUrl) || (await azureAppLinkGenerator())
        }
      ]
    }

    const config = [
      {
        id: 'jira-app-link',
        label: t('common.link_for_platform_users', { platformName: t('app.platform.jira') }),
        path: toValue(localDevelopmentUrl) || (await jiraLinkGenerator()),
        isCurrentPlatform: Boolean(isJiraApp),
        isConnected: () => isJiraConnected.value,
        hasAccess: () => true // hasAccessToJira.value
      },
      {
        id: 'web-app-link',
        label: t('common.link_for_platform_users', { platformName: t('app.platform.web_app') }),
        path: toValue(localDevelopmentUrl) || (await webAppLinkGenerator()),
        isCurrentPlatform: Boolean(isWebApp),
        isConnected: () => isWebAppConnected.value,
        hasAccess: () => true
      }
    ]

    const result = config
      .filter(p => p.path && (p.isCurrentPlatform || (p.isConnected() && p.hasAccess())))
      .toSorted((a, b) => Number(b.isCurrentPlatform) - Number(a.isCurrentPlatform))

    if (result.length === 1) {
      const [link] = result
      return [{ ...link, label: t('common.direct_link') }]
    }

    return result
  }, [])

  return {
    linksForShare
  }
}
